// What this client actually agreed to pay.
//
// The firm default lives in Firm Settings and most matters just use it.
// This card exists for the two cases the default cannot cover:
//
//   The contract this client signed is not the firm's standard one.
//   Recording that here stops the disbursement quoting a rate nobody
//   agreed to.
//
//   The matter has moved past the tier it was opened on. NOTHING in
//   the app advances a matter from pre-suit to suit-filed — it is set
//   here, by a person. Until this card existed the fee agreement told
//   you to change it and gave you nowhere to do so.
//
// Sits above the settlement card because it decides that card's
// arithmetic.

import React, { useCallback, useEffect, useState } from "react";
import { C } from "../constants.js";
import { apiFetch } from "../api.js";

const TRIGGERS = [
  ["pre_suit", "Pre-suit"],
  ["suit_filed", "Suit filed"],
  ["trial_set", "Trial set"],
  ["trial", "Tried"],
  ["appeal", "Appealed"],
];

const TRIGGER_LABEL = Object.fromEntries(TRIGGERS);

function pct(rate) {
  if (rate == null) return "—";
  // 0.3333 reads as 33.33%, and 0.4 as 40% rather than 40.00%.
  const n = rate * 100;
  return `${Number.isInteger(n) ? n : n.toFixed(2)}%`;
}

export function FeeAgreementCard({ matterId, showNotification }) {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(null);

  const load = useCallback(async () => {
    try {
      setData(await apiFetch(`/api/matters/${matterId}/fee-agreement`));
    } catch (e) {
      setData({ _error: e.message, warnings: [] });
    }
  }, [matterId]);

  useEffect(() => { load(); }, [load]);

  const beginEdit = () => {
    const ag = data?.agreement || {};
    setDraft({
      fee_basis: ag.fee_basis || "gross",
      current_trigger: ag.current_trigger || "pre_suit",
      // Tiers are carried over as-is. A matter that only needs its
      // trigger moved must not lose the rate table on the way.
      tiers: (ag.tiers || []).map(t => ({ trigger: t.trigger, pct: String(t.rate * 100) })),
      flat_pct: ag.flat_rate == null ? "" : String(ag.flat_rate * 100),
    });
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await apiFetch(`/api/matters/${matterId}/fee-agreement`, {
        method: "PUT",
        body: JSON.stringify({
          fee_basis: draft.fee_basis,
          current_trigger: draft.current_trigger,
          // Percentages are typed the way people say them and stored as
          // fractions; the API rejects a bare 33, which is the 100x
          // error worth failing loudly on.
          flat_rate: draft.tiers.length || draft.flat_pct === ""
            ? null : Number(draft.flat_pct) / 100,
          tiers: draft.tiers
            .filter(t => t.pct !== "")
            .map(t => ({ trigger: t.trigger, rate: Number(t.pct) / 100 })),
        }),
      });
      setEditing(false);
      await load();
      showNotification?.("Fee agreement saved for this matter.", "success");
    } catch (e) {
      showNotification?.(`Couldn't save: ${e.message}`, "error");
    }
    setSaving(false);
  };

  if (!data) return null;

  const ag = data.agreement;
  const fromDefault = data.source === "firm_default";
  const box = {
    background: "#fff", border: `1px solid ${C.warmGray}`,
    borderRadius: "8px", padding: "20px 24px", marginBottom: "20px",
  };
  const sel = {
    padding: "6px 10px", borderRadius: "6px",
    border: `1px solid ${C.warmGray}`, fontSize: "13px", background: "#fff",
  };

  return React.createElement("div", { style: box },
    React.createElement("div", {
      style: { display: "flex", alignItems: "baseline", gap: "12px", marginBottom: "4px" },
    },
      React.createElement("h3", {
        style: { margin: 0, fontSize: "15px", color: C.navy },
      }, "Fee agreement"),
      React.createElement("span", { style: { flex: 1 } }),
      !editing && React.createElement("button", {
        onClick: beginEdit,
        style: {
          border: "none", background: "none", cursor: "pointer",
          color: C.textMuted, fontSize: "12px",
        },
      }, ag ? "Change for this matter" : "Set for this matter"),
    ),

    data._error && React.createElement("p", {
      style: { fontSize: "13px", color: C.navy },
    }, data._error),

    !editing && React.createElement("div", null,
      React.createElement("div", { style: { fontSize: "13px", color: C.navy } },
        data.effective_rate == null
          ? "No rate applies at this stage."
          : `${pct(data.effective_rate)} of the `
            + `${data.fee_basis === "gross" ? "gross settlement"
                                            : "settlement after costs"}`),
      React.createElement("div", {
        style: { fontSize: "12px", color: C.textMuted, marginTop: "3px" },
      },
        // Where the number came from matters as much as the number.
        // "33 1/3%" with no provenance invites the reader to assume
        // somebody checked the signed contract.
        (fromDefault ? "From the firm's default terms" : "Set on this matter")
        + (ag?.current_trigger
            ? ` · currently ${TRIGGER_LABEL[ag.current_trigger] || ag.current_trigger}`
            : "")),

      (ag?.tiers || []).length > 0 && React.createElement("div", {
        style: { marginTop: "8px", fontSize: "12px", color: C.textMuted },
      }, ag.tiers.map(t =>
        `${TRIGGER_LABEL[t.trigger] || t.trigger} ${pct(t.rate)}`).join(" · ")),

      (data.warnings || []).map((w, i) =>
        React.createElement("p", {
          key: i,
          style: {
            margin: "10px 0 0", fontSize: "12px", color: C.navy,
            background: C.cream, border: `1px solid ${C.warmGray}`,
            borderRadius: "6px", padding: "8px 10px", lineHeight: 1.5,
          },
        }, w)),
    ),

    editing && draft && React.createElement("div", { style: { marginTop: "10px" } },
      React.createElement("div", {
        style: { display: "flex", gap: "16px", flexWrap: "wrap", alignItems: "flex-end" },
      },
        React.createElement("label", { style: { fontSize: "12px", color: C.textMuted } },
          React.createElement("div", { style: { marginBottom: "4px" } }, "Stage reached"),
          React.createElement("select", {
            value: draft.current_trigger, style: sel,
            onChange: e => setDraft({ ...draft, current_trigger: e.target.value }),
          }, TRIGGERS.map(([v, l]) =>
            React.createElement("option", { key: v, value: v }, l)))),

        React.createElement("label", { style: { fontSize: "12px", color: C.textMuted } },
          React.createElement("div", { style: { marginBottom: "4px" } }, "Fee taken on"),
          React.createElement("select", {
            value: draft.fee_basis, style: sel,
            onChange: e => setDraft({ ...draft, fee_basis: e.target.value }),
          },
            React.createElement("option", { value: "gross" }, "Gross settlement"),
            React.createElement("option", { value: "net_of_costs" }, "After case costs"))),

        draft.tiers.length === 0 && React.createElement("label", {
          style: { fontSize: "12px", color: C.textMuted },
        },
          React.createElement("div", { style: { marginBottom: "4px" } }, "Rate %"),
          React.createElement("input", {
            type: "number", step: "0.01", placeholder: "33.33", style: { ...sel, width: "90px" },
            value: draft.flat_pct,
            onChange: e => setDraft({ ...draft, flat_pct: e.target.value }),
          })),
      ),

      draft.tiers.length > 0 && React.createElement("div", {
        style: { marginTop: "12px" },
      },
        React.createElement("div", {
          style: { fontSize: "12px", color: C.textMuted, marginBottom: "6px" },
        }, "Rates by stage"),
        draft.tiers.map((t, i) => React.createElement("div", {
          key: i,
          style: { display: "flex", gap: "8px", alignItems: "center", marginBottom: "6px" },
        },
          React.createElement("select", {
            value: t.trigger, style: sel,
            onChange: e => setDraft({
              ...draft,
              tiers: draft.tiers.map((x, j) =>
                j === i ? { ...x, trigger: e.target.value } : x),
            }),
          }, TRIGGERS.map(([v, l]) =>
            React.createElement("option", { key: v, value: v }, l))),
          React.createElement("input", {
            type: "number", step: "0.01", style: { ...sel, width: "90px" },
            value: t.pct,
            onChange: e => setDraft({
              ...draft,
              tiers: draft.tiers.map((x, j) =>
                j === i ? { ...x, pct: e.target.value } : x),
            }),
          }),
          React.createElement("span", { style: { fontSize: "12px", color: C.textMuted } }, "%"),
        )),
      ),

      React.createElement("p", {
        style: { fontSize: "12px", color: C.textMuted, margin: "12px 0 10px", lineHeight: 1.5 },
      }, "Saving this replaces the firm default for this matter only. "
       + "It is the contract with this client — it should match what they signed."),

      React.createElement("button", {
        onClick: save, disabled: saving,
        style: {
          padding: "7px 14px", fontSize: "13px", cursor: "pointer",
          background: C.navy, color: "#fff", border: "none", borderRadius: "5px",
        },
      }, saving ? "Saving…" : "Save for this matter"),
      React.createElement("button", {
        onClick: () => setEditing(false),
        style: {
          marginLeft: "8px", padding: "7px 14px", fontSize: "13px",
          cursor: "pointer", background: "transparent", color: C.textMuted,
          border: `1px solid ${C.warmGray}`, borderRadius: "5px",
        },
      }, "Cancel"),
    ),
  );
}
